/**
 * Layer table editing.
 *
 * The v6 format has no layer identifier: a layer *is* its position in the file
 * (OGfdi-cad01.cpp counts `lyrcnt++` as it reads them), and every primitive
 * refers to it by that number. So inserting, deleting or reordering a layer
 * renumbers others, and every entity referring to a moved layer has to be
 * remapped in the same operation or the drawing silently changes meaning —
 * copper becoming silkscreen, traces vanishing from the netlist.
 *
 * Each function here therefore does the table edit and the remap together.
 * None of them can be safely composed from a plain array splice.
 */

import {
  type CadDocument, type Entity, type Layer, LayerType,
} from '../model.ts';
import { PAD_MASTER_LAYER } from './board.ts';

export interface LayerPatch {
  name?: string;
  type?: LayerType;
  bottom?: boolean;
  inner?: boolean;
  screen?: number;
  print?: number;
  screenNegative?: boolean;
  printNegative?: boolean;
  visible?: boolean;
  locked?: boolean;
  net?: string;
}

// ---------------------------------------------------------------------------
// Remapping
// ---------------------------------------------------------------------------

/** Apply a layer-number mapping to every primitive, components included. */
export function remapEntityLayers(
  entities: Entity[],
  map: (layer: number) => number,
): number {
  let changed = 0;
  const walk = (list: Entity[]): void => {
    for (const e of list) {
      if (e.kind === 'component') {
        walk(e.children);
        // A component's own layer number is meaningful too.
        const next = map(e.layer);
        if (next !== e.layer) { e.layer = next; changed++; }
        continue;
      }
      const next = map(e.layer);
      if (next !== e.layer) { e.layer = next; changed++; }
    }
  };
  walk(entities);
  return changed;
}

/** Renumber `layer.num` to match array position, after any table edit. */
function renumber(layers: Layer[]): void {
  layers.forEach((l, i) => { l.num = i; });
}

/** How many primitives sit on a layer, components included. */
export function countEntitiesOn(doc: CadDocument, layer: number): number {
  let n = 0;
  const walk = (list: Entity[]): void => {
    for (const e of list) {
      if (e.layer === layer) n++;
      if (e.kind === 'component') walk(e.children);
    }
  };
  walk(doc.entities);
  return n;
}

// ---------------------------------------------------------------------------
// Operations
// ---------------------------------------------------------------------------

/**
 * Insert a layer at `at`, pushing later layers up one number.
 *
 * The original inhibits inserting at position zero — layer 0 is the pad
 * master and everything else is defined relative to it (OGdlg07.cpp:1157).
 */
export function insertLayer(
  doc: CadDocument,
  at: number,
  init: LayerPatch = {},
): number {
  const index = Math.max(1, Math.min(at, doc.layers.length));

  const layer: Layer = {
    num: index,
    name: init.name ?? `${index} Layer`,
    // The original's new layers default to Drawing on the top side, which
    // plots nothing until you say what it is.
    type: init.type ?? LayerType.Drawing,
    bottom: init.bottom ?? false,
    screen: init.screen ?? 0xffffff,
    print: init.print ?? 0x000000,
    screenNegative: init.screenNegative ?? false,
    printNegative: init.printNegative ?? false,
    visible: init.visible ?? true,
    locked: init.locked ?? false,
  };

  doc.layers.splice(index, 0, layer);
  renumber(doc.layers);
  // Everything at or after the insertion point shifts up one.
  remapEntityLayers(doc.entities, (l) => (l >= index ? l + 1 : l));

  return index;
}

export interface DeleteLayerResult {
  /** Entities that were on the layer. */
  affected: number;
  /** True when those entities were moved rather than deleted. */
  moved: boolean;
}

/**
 * Delete a layer.
 *
 * Geometry on it must go somewhere: either onto `moveTo`, or away entirely.
 * Silently dropping it would lose work, and silently keeping it would leave
 * entities pointing at a layer that no longer exists.
 */
export function deleteLayer(
  doc: CadDocument,
  index: number,
  opts: { moveTo?: number } = {},
): DeleteLayerResult {
  if (index === PAD_MASTER_LAYER) {
    throw new Error('Layer zero is the pad master and cannot be deleted.');
  }
  if (index < 0 || index >= doc.layers.length) {
    throw new Error(`No layer ${index}.`);
  }

  const affected = countEntitiesOn(doc, index);
  const moveTo = opts.moveTo;

  if (moveTo !== undefined) {
    if (moveTo === index) throw new Error('Cannot move geometry onto the layer being deleted.');
    remapEntityLayers(doc.entities, (l) => (l === index ? moveTo : l));
  } else {
    const drop = (list: Entity[]): Entity[] => {
      const kept = list.filter((e) => e.layer !== index);
      for (const e of kept) if (e.kind === 'component') e.children = drop(e.children);
      return kept;
    };
    doc.entities = drop(doc.entities);
  }

  doc.layers.splice(index, 1);
  renumber(doc.layers);
  // Layers above the hole shift down one; anything still pointing at the
  // deleted number was remapped above.
  remapEntityLayers(doc.entities, (l) => (l > index ? l - 1 : l));

  return { affected, moved: moveTo !== undefined };
}

/**
 * Move a layer to a new position, renumbering and remapping accordingly.
 *
 * Layer 0 neither moves nor is displaced, because the pad master's number is
 * relied on by every through-hole pad in the libraries.
 */
export function moveLayer(doc: CadDocument, from: number, to: number): void {
  if (from === PAD_MASTER_LAYER || to === PAD_MASTER_LAYER) {
    throw new Error('Layer zero is the pad master and cannot be moved.');
  }
  if (from < 0 || from >= doc.layers.length) throw new Error(`No layer ${from}.`);
  const target = Math.max(1, Math.min(to, doc.layers.length - 1));
  if (from === target) return;

  const moved = doc.layers.splice(from, 1)[0]!;
  doc.layers.splice(target, 0, moved);
  renumber(doc.layers);

  // Build the old->new mapping from the same shift the splice performed,
  // rather than inferring it, so the two can never disagree.
  remapEntityLayers(doc.entities, (l) => {
    if (l === from) return target;
    if (from < target) return l > from && l <= target ? l - 1 : l;
    return l >= target && l < from ? l + 1 : l;
  });
}

/** Change a layer's properties. Does not affect numbering. */
export function updateLayer(doc: CadDocument, index: number, patch: LayerPatch): void {
  const layer = doc.layers[index];
  if (!layer) throw new Error(`No layer ${index}.`);
  Object.assign(layer, patch);
  layer.num = index;
}

/**
 * Reorder the whole table in one step.
 * `order` lists the current layer indices in their new sequence.
 */
export function reorderLayers(doc: CadDocument, order: readonly number[]): void {
  if (order.length !== doc.layers.length) {
    throw new Error('Order must list every layer exactly once.');
  }
  if (order[0] !== PAD_MASTER_LAYER) {
    throw new Error('Layer zero is the pad master and must stay first.');
  }
  const seen = new Set(order);
  if (seen.size !== order.length) throw new Error('Order repeats a layer.');

  const next = order.map((old) => doc.layers[old]!);
  const map = new Map<number, number>();
  order.forEach((old, index) => map.set(old, index));

  doc.layers = next;
  renumber(doc.layers);
  remapEntityLayers(doc.entities, (l) => map.get(l) ?? l);
}

// ---------------------------------------------------------------------------
// Fitting the table to what a document actually contains
// ---------------------------------------------------------------------------

export interface PruneResult {
  /** Names of the layers that were dropped, in the order they were removed. */
  removed: string[];
  /** How many layers remain. */
  kept: number;
}

/**
 * Drop layers nothing is drawn on.
 *
 * The stock stack declares sixteen layers — four mid layers, two planes, a
 * ratnest and a CAM control layer — whether or not a board uses them, because
 * that is the table CIRCAD ships. That is right for a CIRCAD file, which
 * states its own stack, and wrong for anything imported from a foreign format,
 * where the stack is ours to invent: a two-sided board taken in from Gerber or
 * KiCad should show two copper layers, not sixteen of which fourteen are
 * empty.
 *
 * The pad master always stays, whether or not anything sits on it. It is layer
 * zero by definition — every through-hole pad in every library refers to it by
 * that number, and the renderer draws through pads there — so it is structure,
 * not content.
 *
 * `keep` names layers to spare regardless. Use it for a layer that was
 * genuinely supplied and is genuinely blank: a paste film with no apertures on
 * it is a fact about the board, and dropping it would claim the film was never
 * sent.
 */
export function pruneUnusedLayers(
  doc: CadDocument,
  opts: { keep?: readonly string[] } = {},
): PruneResult {
  const spare = new Set((opts.keep ?? []).map((n) => n.toLowerCase()));

  const used = new Set<number>([PAD_MASTER_LAYER]);
  const walk = (list: Entity[]): void => {
    for (const e of list) {
      used.add(e.layer);
      if (e.kind === 'component') walk(e.children);
    }
  };
  walk(doc.entities);

  const removed: string[] = [];
  const keptLayers: Layer[] = [];
  // old index → new index, or -1 for a layer that is going.
  const map = new Map<number, number>();

  doc.layers.forEach((layer, i) => {
    if (used.has(i) || spare.has(layer.name.toLowerCase())) {
      map.set(i, keptLayers.length);
      keptLayers.push(layer);
    } else {
      map.set(i, -1);
      removed.push(layer.name);
    }
  });

  if (removed.length === 0) return { removed, kept: doc.layers.length };

  doc.layers = keptLayers;
  renumber(doc.layers);
  // Nothing is drawn on a removed layer, so no entity can map to -1 here; the
  // fallback keeps a corrupt document from pointing at a layer that is gone.
  remapEntityLayers(doc.entities, (l) => {
    const next = map.get(l);
    return next === undefined || next < 0 ? PAD_MASTER_LAYER : next;
  });
  remapSettings(doc, map);

  return { removed, kept: doc.layers.length };
}

/**
 * Move the layer numbers held in document settings, not just in geometry.
 *
 * `Display::`, the primitive defaults and the Gerber and autorouter layer
 * lists all store layer *numbers*. Renumbering the table without these leaves
 * the active layer and every plot definition pointing somewhere else.
 */
function remapSettings(doc: CadDocument, map: Map<number, number>): void {
  const move = (l: number): number => {
    const next = map.get(l);
    return next === undefined || next < 0 ? PAD_MASTER_LAYER : next;
  };
  // Settings may name a layer as a string, which survives renumbering intact.
  const moveRef = (v: number | string | undefined): number | string | undefined =>
    (typeof v === 'number' ? move(v) : v);

  if (doc.display) doc.display.layer = moveRef(doc.display.layer) ?? 0;

  for (const key of ['line', 'fill', 'arc', 'text', 'via'] as const) {
    const d = doc.defaults[key];
    if (d && 'layer' in d && d.layer !== undefined) d.layer = moveRef(d.layer);
  }

  const list = (nums: number[]): number[] =>
    [...new Set(nums.map((n) => map.get(n) ?? -1).filter((n) => n >= 0))];

  for (const f of doc.gerberFiles ?? []) f.layers = list(f.layers);
  if (doc.autoRoute) doc.autoRoute.layers = list(doc.autoRoute.layers);
}

/**
 * Bring a component from another layer table onto this document's.
 *
 * A library part carries the layer *numbers* of the stack it was drawn
 * against, and a number means nothing without the table it indexes. Placing a
 * footprint drawn against the stock stack onto a document whose table has been
 * fitted to its contents would otherwise put the reference designator on
 * whatever layer now happens to sit at index three.
 *
 * Matching is by name, which is what actually identifies a layer across two
 * stacks. A layer the part needs and the document lacks is added rather than
 * folded onto another: a footprint with a paste aperture placed on a board
 * with no paste layer should bring one with it.
 */
export function adoptLayers(
  entity: Entity,
  doc: CadDocument,
  source: readonly Layer[],
): number {
  const cache = new Map<number, number>();

  const target = (from: number): number => {
    const hit = cache.get(from);
    if (hit !== undefined) return hit;

    const want = source[from];
    if (!want) {
      // A number with no layer behind it in the source table. The pad master
      // is the safe home: it is the one layer guaranteed to exist.
      cache.set(from, PAD_MASTER_LAYER);
      return PAD_MASTER_LAYER;
    }

    const found = doc.layers.findIndex(
      (l) => l.name.toLowerCase() === want.name.toLowerCase(),
    );
    if (found >= 0) { cache.set(from, found); return found; }

    const added = doc.layers.length;
    doc.layers.push({ ...structuredClone(want), num: added, visible: true, locked: false });
    cache.set(from, added);
    return added;
  };

  return remapEntityLayers([entity], target);
}

/**
 * Work out which copper is buried, for a stack that could not say.
 *
 * A CIRCAD file has no bit for it — an inner copper layer is stored exactly
 * like the outer one — so a file that has never been through SirCAD arrives
 * with every mid layer and every plane looking like top-side copper. Left
 * alone, the realistic views paint all of it onto the top face: across the
 * boards here that is some thirty-eight thousand objects on the wrong side of
 * the laminate.
 *
 * The rule is the stack's own order, which is how the original lays it out and
 * how a fabricator reads it: the first top-side copper is the outer top, the
 * first bottom-side copper is the outer bottom, and copper anywhere else is in
 * between. A plane is a power plane, which is buried by definition.
 *
 * Only fills in what is not already stated, so a stack the user has arranged
 * by hand is never quietly rearranged.
 */
export function inferInnerLayers(layers: Layer[]): void {
  let outerTop = -1;
  let outerBottom = -1;
  for (const l of layers) {
    if (l.num === PAD_MASTER_LAYER || l.type !== LayerType.Copper) continue;
    if (l.bottom) { if (outerBottom < 0) outerBottom = l.num; }
    else if (outerTop < 0) outerTop = l.num;
  }

  for (const l of layers) {
    if (l.inner !== undefined) continue;
    if (l.num === PAD_MASTER_LAYER) { l.inner = false; continue; }
    if (l.type === LayerType.Plane) { l.inner = true; continue; }
    if (l.type !== LayerType.Copper) { l.inner = false; continue; }
    l.inner = l.num !== outerTop && l.num !== outerBottom;
  }
}
